import AdminPanelSettingsIcon from '@material-design-icons/svg/filled/admin_panel_settings.svg?react';
import GroupIcon from '@material-design-icons/svg/filled/group.svg?react';
import KeyboardArrowDownIcon from '@material-design-icons/svg/filled/keyboard_arrow_down.svg?react';
import MailIcon from '@material-design-icons/svg/filled/mail.svg?react';
import PersonIcon from '@material-design-icons/svg/filled/person.svg?react';
import RemoveRedEyeIcon from '@material-design-icons/svg/filled/remove_red_eye.svg?react';
import StarIcon from '@material-design-icons/svg/filled/star.svg?react';
import { ComponentType, FormEvent, useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';
import { useAsyncData } from '../../hooks/useAsyncData';
import { usePageTitle } from '../../hooks/usePageTitle';
import { usePublisher } from '../../hooks/usePublisher';
import { useTRPC } from '../../hooks/useTRPC';
import { isLeftResult } from '../../lib/trpcTypeGuards';
import { unknownToLeft } from '../../lib/unknownToLeft';
import type { RightResult, RouterOutputs } from '../../types/TRPC';
import { captureAndReportError } from '../../utils/errorReporting';
import {
  canResendPublisherInvite,
  getPublisherInviteStateClasses,
  getPublisherInviteStateLabel,
} from '../../utils/publisherInvites';
import { getTeamManagementPermissionMessage } from '../../utils/apiKeyAccess';
import { Input } from '../Input';
import { Button } from '../ui/button';

interface RoleChipStyle {
  icon: ComponentType<{ className?: string }>;
  iconClassName: string;
  textClassName: string;
  containerClassName: string;
}

const ROLE_CHIP_STYLES: Record<string, RoleChipStyle> = {
  Owner: {
    icon: StarIcon,
    iconClassName: 'fill-blue-400',
    textClassName: 'text-blue-400',
    containerClassName: 'bg-blue-900/40 border-blue-700/40',
  },
  Admin: {
    icon: AdminPanelSettingsIcon,
    iconClassName: 'fill-neutral-400',
    textClassName: 'text-neutral-400',
    containerClassName: 'bg-neutral-800/60 border-neutral-700/50',
  },
  Member: {
    icon: PersonIcon,
    iconClassName: 'fill-neutral-400',
    textClassName: 'text-neutral-400',
    containerClassName: 'bg-neutral-800/60 border-neutral-700/50',
  },
  Viewer: {
    icon: RemoveRedEyeIcon,
    iconClassName: 'fill-neutral-400',
    textClassName: 'text-neutral-400',
    containerClassName: 'bg-neutral-800/60 border-neutral-700/50',
  },
};

function RoleChip({ role }: { role: string }) {
  const style = ROLE_CHIP_STYLES[role] ?? ROLE_CHIP_STYLES.Member;
  const Icon = style.icon;
  return (
    <div
      className={`inline-flex items-center justify-center rounded-full border px-2 py-0.5 text-xs font-medium w-fit whitespace-nowrap shrink-0 gap-1 transition-[color,box-shadow] overflow-hidden select-none ${style.containerClassName}`}
    >
      <Icon className={`size-3 pointer-events-none ${style.iconClassName}`} />
      <span className={style.textClassName}>{role}</span>
    </div>
  );
}

type TeamMember = RightResult<
  RouterOutputs['listPublisherMembers']
>['members'][number];
type TeamInvite = RightResult<
  RouterOutputs['listPublisherPendingInvites']
>['invites'][number];

const INVITE_ROLE_OPTIONS = [
  { value: 'Admin', label: 'Admin' },
  { value: 'Member', label: 'Member' },
  { value: 'Viewer', label: 'Viewer' },
] as const;

export function TeamSettingsPage() {
  const navigate = useNavigate();
  const trpc = useTRPC();
  const { activePublisher, isLoading: isPublisherLoading } = usePublisher();
  const {
    data: members,
    setData: setMembers,
    isLoading: loading,
    setIsLoading,
    run,
  } = useAsyncData<TeamMember[]>({
    initialData: [],
    initialLoading: true,
  });
  const [invites, setInvites] = useState<TeamInvite[]>([]);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] =
    useState<(typeof INVITE_ROLE_OPTIONS)[number]['value']>('Member');
  const [inviting, setInviting] = useState(false);
  const [inviteActionId, setInviteActionId] = useState<string | null>(null);
  const accessError =
    !isPublisherLoading && activePublisher
      ? getTeamManagementPermissionMessage(activePublisher.canManageTeam)
      : null;

  usePageTitle('Team | Publishing Portal');

  async function loadTeam() {
    if (!activePublisher) {
      setMembers([]);
      setInvites([]);
      setIsLoading(false);
      return;
    }

    await run(
      () =>
        Promise.all([
          trpc.listPublisherMembers.query(undefined).catch(unknownToLeft),
          trpc.listPublisherPendingInvites
            .query(undefined)
            .catch(unknownToLeft),
        ]),
      {
        onSuccess: ([membersResult, invitesResult], helpers) => {
          if (isLeftResult(membersResult)) {
            toast.error('Failed to load team settings', {
              description: membersResult.left.message,
              duration: 5000,
            });
            captureAndReportError(membersResult.left);
            return;
          }

          if (isLeftResult(invitesResult)) {
            toast.error('Failed to load team settings', {
              description: invitesResult.left.message,
              duration: 5000,
            });
            captureAndReportError(invitesResult.left);
            return;
          }

          helpers.setData(membersResult.right.members);
          setInvites(invitesResult.right.invites);
        },
      },
    );
  }

  useEffect(() => {
    if (isPublisherLoading) {
      return;
    }

    if (!accessError) {
      return;
    }

    toast.error('Team management permission required', {
      description: accessError,
      duration: 6000,
    });
    navigate('/dashboard/settings/preferences', {
      replace: true,
    });
  }, [accessError, isPublisherLoading, navigate]);

  useEffect(() => {
    if (isPublisherLoading) {
      return;
    }

    if (accessError) {
      return;
    }

    void loadTeam();
  }, [accessError, activePublisher?.publisherUserJoinId, isPublisherLoading]);

  async function handleInviteSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    if (!inviteEmail.trim()) {
      toast.error('Enter an email address');
      return;
    }

    setInviting(true);

    const result = await trpc.invitePublisherMember
      .mutate({
        email: inviteEmail.trim(),
        role: inviteRole,
        permissions: [],
      })
      .catch(unknownToLeft);

    setInviting(false);

    if (isLeftResult(result)) {
      toast.error('Failed to send invite', {
        description: result.left.message,
        duration: 5000,
      });
      captureAndReportError(result.left);
      return;
    }

    switch (result.right.status) {
      case 'created':
        toast.success('Invite email sent', {
          description: `An invite was sent to ${inviteEmail.trim()}.`,
          duration: 4000,
        });
        setInviteEmail('');
        break;
      case 'resent':
        toast.success('Invite email re-sent', {
          description: `A fresh invite was sent to ${inviteEmail.trim()}.`,
          duration: 4000,
        });
        setInviteEmail('');
        break;
      case 'already_member':
        toast.error('Already a team member', {
          description: `${inviteEmail.trim()} already has publisher access.`,
          duration: 5000,
        });
        break;
      case 'blocked_deleted_account':
        toast.error('Invite blocked', {
          description:
            'This email belongs to a previously removed account. Contact support to restore access before inviting it again.',
          duration: 6000,
        });
        break;
      case 'blocked_wrong_state':
        toast.error('Invite could not be sent', {
          description: 'This invite is no longer in a sendable state.',
          duration: 5000,
        });
        break;
    }

    await loadTeam();
  }

  async function handleResend(inviteId: string) {
    setInviteActionId(inviteId);

    const result = await trpc.resendPublisherInvite
      .mutate({ inviteId })
      .catch(unknownToLeft);

    setInviteActionId(null);

    if (isLeftResult(result)) {
      toast.error('Failed to resend invite', {
        description: result.left.message,
        duration: 5000,
      });
      captureAndReportError(result.left);
      return;
    }

    switch (result.right.status) {
      case 'resent':
        toast.success('Invite email re-sent');
        break;
      case 'already_member':
        toast.error('Invite no longer needed', {
          description: 'This email already has publisher access.',
          duration: 5000,
        });
        break;
      case 'blocked_deleted_account':
        toast.error('Invite blocked', {
          description:
            'This email belongs to a previously removed account. Contact support to restore access first.',
          duration: 6000,
        });
        break;
      case 'blocked_wrong_state':
        toast.error('Invite cannot be re-sent', {
          description: 'Only pending or expired invites can be re-sent.',
          duration: 5000,
        });
        break;
      case 'created':
        toast.success('Invite email sent');
        break;
    }

    await loadTeam();
  }

  async function handleRevoke(inviteId: string) {
    setInviteActionId(inviteId);

    const result = await trpc.revokePublisherInvite
      .mutate({ inviteId })
      .catch(unknownToLeft);

    setInviteActionId(null);

    if (isLeftResult(result)) {
      toast.error('Failed to revoke invite', {
        description: result.left.message,
        duration: 5000,
      });
      captureAndReportError(result.left);
      return;
    }

    toast.success('Invite revoked');
    await loadTeam();
  }

  if (isPublisherLoading || loading) {
    return (
      <div className="flex h-full w-full items-center justify-center bg-neutral-900">
        <div className="text-neutral-400">Loading team settings…</div>
      </div>
    );
  }

  if (accessError) {
    return null;
  }

  if (!activePublisher) {
    return (
      <div className="flex h-full w-full items-center justify-center bg-neutral-900">
        <div className="text-red-500">No active publisher found</div>
      </div>
    );
  }

  return (
    <div className="flex h-full w-full flex-col">
      <div className="sticky top-0 z-40 h-[72px] shrink-0 bg-neutral-900">
        <div className="flex h-full items-center px-10">
          <div className="flex items-center gap-2">
            <GroupIcon className="size-5 fill-white" />
            <span className="text-base font-medium text-white">Team</span>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        <div className="p-10">
          <div className="mx-auto max-w-2xl">
            <div className="mb-8 text-sm leading-relaxed text-neutral-400">
              Invite teammates to{' '}
              <span className="text-white">{activePublisher.name}</span>, review
              current access, and manage pending email invitations without
              changing your active publisher.
            </div>

            <div className="mb-8 rounded-2xl border border-neutral-800 bg-neutral-950/60 p-6">
              <div className="mb-4 flex items-center gap-2">
                <MailIcon className="size-5 fill-white" />
                <h2 className="text-lg font-semibold text-white">
                  Invite teammate
                </h2>
              </div>

              <form
                className="grid gap-4 md:grid-cols-[minmax(0,1fr)_180px_auto]"
                onSubmit={(event) => void handleInviteSubmit(event)}
              >
                <Input
                  type="email"
                  value={inviteEmail}
                  onChange={(event) => setInviteEmail(event.target.value)}
                  placeholder="teammate@example.com"
                  autoComplete="email"
                />

                <div className="relative">
                  <select
                    value={inviteRole}
                    onChange={(event) =>
                      setInviteRole(
                        event.target
                          .value as (typeof INVITE_ROLE_OPTIONS)[number]['value'],
                      )
                    }
                    style={{ colorScheme: 'dark' }}
                    className="h-10 w-full appearance-none rounded-xl border border-neutral-700 bg-transparent pl-4 pr-10 text-sm text-white outline-none transition-colors hover:border-neutral-500 focus:border-neutral-300"
                  >
                    {INVITE_ROLE_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  <KeyboardArrowDownIcon className="pointer-events-none absolute right-3 top-1/2 size-4 -translate-y-1/2 fill-neutral-400" />
                </div>

                <Button
                  type="submit"
                  disabled={inviting}
                  size="lg"
                  className="bg-teal-600 px-5 text-white hover:bg-teal-500"
                >
                  {inviting ? 'Sending...' : 'Send Invite'}
                </Button>
              </form>
            </div>

            <div className="mb-8 rounded-2xl border border-neutral-800 bg-neutral-950/60 p-6">
              <div className="mb-4 flex items-center justify-between gap-3">
                <h2 className="text-lg font-semibold text-white">
                  Pending invites
                </h2>
                <div className="text-sm text-neutral-500">
                  {invites.length} open
                </div>
              </div>

              {invites.length === 0 ? (
                <div className="rounded-xl border border-dashed border-neutral-800 px-4 py-6 text-sm text-neutral-500">
                  No pending team invites.
                </div>
              ) : (
                <div className="space-y-3">
                  {invites.map((invite) => (
                    <div
                      key={invite.id}
                      className="rounded-xl border border-neutral-800 bg-neutral-900/80 p-4"
                    >
                      <div className="mb-3 flex flex-wrap items-start justify-between gap-3">
                        <div>
                          <div className="text-sm font-medium text-white">
                            {invite.invitedEmail}
                          </div>
                          <div className="mt-1 text-sm text-neutral-400">
                            {invite.role} access
                            {invite.invitedByName
                              ? ` • invited by ${invite.invitedByName}`
                              : ''}
                          </div>
                        </div>

                        <div
                          className={`rounded-full border px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] ${getPublisherInviteStateClasses(invite.state)}`}
                        >
                          {getPublisherInviteStateLabel(invite.state)}
                        </div>
                      </div>

                      <div className="mb-4 text-xs text-neutral-500">
                        Expires {new Date(invite.expiresAt).toLocaleString()}
                      </div>

                      <div className="flex flex-wrap gap-3">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          disabled={
                            inviteActionId === invite.id ||
                            !canResendPublisherInvite(invite)
                          }
                          onClick={() => void handleResend(invite.id)}
                        >
                          Re-send
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          className="bg-red-600 text-white hover:bg-red-500"
                          disabled={inviteActionId === invite.id}
                          onClick={() => void handleRevoke(invite.id)}
                        >
                          Revoke
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="rounded-2xl border border-neutral-800 bg-neutral-950/60 p-6">
              <div className="mb-4 flex items-center justify-between gap-3">
                <h2 className="text-lg font-semibold text-white">
                  Current members
                </h2>
                <div className="text-sm text-neutral-500">
                  {members.length} members
                </div>
              </div>

              <div className="space-y-3">
                {members.map((member) => (
                  <div
                    key={member.publisherUserJoinId}
                    className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-neutral-800 bg-neutral-900/80 p-4"
                  >
                    <div>
                      <div className="text-sm font-medium text-white">
                        {member.fullName}
                      </div>
                      <div className="mt-1 text-sm text-neutral-400">
                        {member.email}
                      </div>
                    </div>

                    <RoleChip role={member.isOwner ? 'Owner' : member.role} />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
